"use client";

import { ArrowRight } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Progress } from "@/components/ui/progress";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Funding() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const steps = [{
    num: "01",
    label: "Submit",
    desc: "Provide deal specifics"
  }, {
    num: "02",
    label: "Review",
    desc: "Underwriting assessment"
  }, {
    num: "03",
    label: "Fund",
    desc: "Capital deployment"
  }];
  return <section data-section-id="4538" id="engine-01-full-stack-funding" className="bg-background text-foreground relative overflow-hidden pt-28 md:pt-32 lg:pt-[140px] pb-20 md:pb-28 lg:pb-32 px-6 md:px-10 lg:px-16">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />

      <div className="relative max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {}
          <div ref={leftRef} className={`lg:col-span-7 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {}
            <div className="mb-12 md:mb-16">
              <div className="flex items-center justify-between mb-4">
                <span className="font-mono text-[11px] md:text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  Engine 01 — Full-Stack Funding
                </span>
                <span className="font-mono text-[11px] md:text-xs uppercase tracking-[0.2em] text-primary">
                  33%
                </span>
              </div>
              <Progress value={33} className="h-px bg-border/40 rounded-md" />
            </div>

            {}
            <h1 className="font-default font-bold text-foreground leading-[0.95] tracking-tight mb-10 md:mb-12" style={{
            fontSize: "clamp(3.5rem, 9vw, 7rem)"
          }}>
              Capital Solutions, Engineered.
            </h1>

            {}
            <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground max-w-[440px] mb-14 md:mb-16">
              We fund the whole deal. From first mortgage debt to second-position gap equity, our capital infrastructure ensures you close with little to none of your own cash. Fix-and-Flip, BRRRR, or New Construction — we are the architect of your capital stack.
            </p>

            {}
            <div className="mb-16 md:mb-20">
              <Link to="/PreQualificationFormPage">
                <Button variant="outline" size="lg" className="group border-border bg-secondary text-secondary-foreground hover:bg-secondary/90 font-default font-bold text-xs uppercase tracking-[0.18em] h-12 px-7">
                  Get Pre-Qualified
                  <ArrowRight className="ml-1 size-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>

            {}
            <div className="relative">
              <div aria-hidden="true" className="absolute top-3 left-0 right-0 h-px bg-border/50" />
              <ol className="grid grid-cols-3 gap-4 md:gap-8 relative">
                {steps.map((step, i) => <li key={step.num} data-index={i} className="relative pt-7">
                    <span aria-hidden="true" className={`absolute top-[6px] left-0 size-2 rounded-none ${i === 0 ? "bg-primary" : "bg-border"}`} />
                    <div className="font-mono text-[11px] md:text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                      {step.num}
                    </div>
                    <div className="font-default font-semibold text-lg md:text-xl text-foreground mb-1">
                      {step.label}
                    </div>
                    <div className="font-default text-xs md:text-sm text-muted-foreground leading-snug max-w-[180px]">
                      {step.desc}
                    </div>
                  </li>)}
              </ol>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-5 lg:pt-8 transition-all duration-1000 delay-200 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative">
              <AspectRatio ratio={3 / 4} className="overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/real_estate_capital_vault.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/real_estate_capital_vault.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/real_estate_capital_vault.png-wvc-sizes" alt="High-contrast architectural shot of a modern real estate development site at blue hour" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                {}
                <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-transparent" />
              </AspectRatio>

              {}
              <div className="mt-5 flex items-start gap-4">
                <span aria-hidden="true" className="mt-2 block h-px w-8 bg-border flex-shrink-0" />
                <p className="font-mono text-[11px] md:text-xs uppercase tracking-[0.2em] text-muted-foreground leading-relaxed">
                  Capital Infrastructure — 2024
                  <br />
                  <span className="text-muted-foreground/60 normal-case tracking-normal font-default italic text-xs">
                    BeneWealth Financial Vault
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}